/**
 * sx `borderRadius` resolver: string keys → theme.shape.radius; numbers keep MUI × borderRadius.
 *
 * @example
 * sx={{ borderRadius: 'lg' }}           // → 8px
 * sx={{ borderRadius: 'rounded' }}      // → 50%
 * sx={{ borderRadius: 2 }}              // → 8px (2 × shape.borderRadius)
 * sx={{ borderRadius: '12px' }}         // passthrough
 */
import { handleBreakpoints } from '@mui/system/breakpoints';
import type { Theme } from '@mui/material/styles';

type RadiusMap = Theme['shape']['radius'];

function resolveBorderRadius(theme: Theme, value: unknown): string | number {
  if (typeof value === 'string') {
    const token = (theme.shape.radius as RadiusMap)[value as keyof RadiusMap];
    if (token !== undefined) {
      return typeof token === 'number' ? `${token}px` : token;
    }
    return value;
  }
  if (typeof value === 'number') {
    return theme.shape.borderRadius * value;
  }
  return value as string | number;
}

export function borderRadiusSxStyle(props: { borderRadius?: unknown; theme: Theme }) {
  const { borderRadius, theme } = props;
  if (borderRadius == null) {
    return null;
  }

  return handleBreakpoints(props, borderRadius, (value) => ({
    borderRadius: resolveBorderRadius(theme, value),
  }));
}

/** Spread into createTheme({ unstable_sxConfig }). Merged over MUI defaults. */
export const borderRadiusSxConfig = {
  borderRadius: {
    style: borderRadiusSxStyle,
  },
};
